import { ethers } from "ethers";
import { BN254_PRIME } from "./handle-registration-hooks";
import {
  HANDLE_REGISTRY_CHAIN_ID,
  handleRegistryAddress,
} from "./handle-networks";
import { normalizeHandle } from "./handle-resolver";

export type HandleWalletSigner = Pick<ethers.Signer, "getAddress"> & {
  requestHandleOwnershipRoot?: () => Promise<string>;
};

/** EVM identifiers are case insensitive; Solana public keys must retain case. */
export function normalizeHandleWallet(wallet: string): string {
  const value = wallet.trim();
  return ethers.isAddress(value) ? value.toLowerCase() : value;
}

/**
 * A handle's owner secret (hsk) can never change on chain: the registry
 * rejects a rotation with a different owner commitment.
 *
 * Registering needs no wallet prompt: the secret comes from the privacy-key
 * signature the wallet already gave at sign-in for this network, so the same
 * wallet re-derives it on this network in any browser. Registration and
 * every rotation are relayed; nothing on chain names the wallet.
 *
 * Only using a handle on another network needs one extra signature: typed
 * data with no chainId, account or contract in it, so the same wallet
 * produces it on every network page. It never leaves the browser; it seals
 * and opens a backup of the secret that another network's page can use.
 */
const SHARING_DOMAIN = Object.freeze({ name: "StealthOS", version: "1" });
const SHARING_TYPES = Object.freeze({
  HandleKey: [
    { name: "purpose", type: "string" },
    { name: "privacy", type: "string" },
  ],
});

export function handleSharingTypedData() {
  return {
    domain: { ...SHARING_DOMAIN },
    types: { HandleKey: [...SHARING_TYPES.HandleKey] },
    message: {
      purpose: "Use your .stealth handle on another network",
      privacy:
        "Stays in this browser. Not a transaction, never sent or stored, and not linked to your wallet on chain.",
    },
  };
}

/** The root that seals and opens this wallet's handle backups. */
export async function requestHandleOwnershipRoot(
  signer: HandleWalletSigner,
): Promise<string> {
  if (signer.requestHandleOwnershipRoot)
    return signer.requestHandleOwnershipRoot();
  const evmSigner = signer as ethers.Signer;
  const account = await signer.getAddress();
  const { domain, types, message } = handleSharingTypedData();
  const signature = await evmSigner.signTypedData(domain, types, message);
  if (
    ethers.verifyTypedData(domain, types, message, signature).toLowerCase() !==
    account.toLowerCase()
  )
    throw new Error("The wallet's handle signature did not verify");
  // Canonical r, s, v bytes, so an equivalent encoding gives the same root.
  return ethers.keccak256(ethers.Signature.from(signature).serialized);
}

const abi = ethers.AbiCoder.defaultAbiCoder();
const scoped = (tag: string, root: string, extra: string[] = []) =>
  ethers.keccak256(
    abi.encode(
      ["string", "uint256", "address", "bytes32", ...extra.map(() => "string")],
      [tag, HANDLE_REGISTRY_CHAIN_ID, handleRegistryAddress(), root, ...extra],
    ),
  );

/**
 * The owner secret of a handle registered on this network: derived from the
 * sign-in privacy-key signature (already in memory), so registering asks for
 * nothing. Domain-separated from the spending keys that signature seeds.
 */
export function deriveNetworkHandleSecret(
  derivationSignatureB64: string,
  handle: string,
): string {
  const name = normalizeHandle(handle);
  if (!name || name !== handle) throw new Error("Invalid handle");
  const material = derivationSignatureB64.trim();
  if (!material)
    throw new Error(
      "Privacy-key signature is not available; reconnect the wallet once",
    );
  const value = BigInt(
    ethers.keccak256(
      abi.encode(
        ["string", "uint256", "address", "bytes", "string"],
        [
          "stealthos-handle-network-owner-v1",
          HANDLE_REGISTRY_CHAIN_ID,
          handleRegistryAddress(),
          Uint8Array.from(atob(material), (c) => c.charCodeAt(0)),
          name,
        ],
      ),
    ),
  );
  const reduced = value % BN254_PRIME;
  if (reduced === 0n) throw new Error("Derived handle secret is zero");
  return `0x${reduced.toString(16).padStart(64, "0")}`;
}

/** Where the data API files this wallet's handles. It names no wallet. */
export function handleOwnerLookupKey(root: string): string {
  return scoped("stealthos-handle-lookup-v1", root).slice(2);
}

async function backupKey(root: string): Promise<CryptoKey> {
  const raw = ethers.getBytes(scoped("stealthos-handle-backup-v1", root));
  return crypto.subtle.importKey(
    "raw",
    Uint8Array.from(raw).buffer,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * A secret that predates derivation (random, saved in one browser), sealed so
 * only this wallet's signature can open it on another network or browser.
 */
export async function wrapHandleSecret(
  root: string,
  handle: string,
  secret: string,
): Promise<string> {
  const plaintext = ethers.getBytes(secret);
  if (plaintext.length !== 32) throw new Error("Invalid handle secret");
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv.buffer,
      additionalData: Uint8Array.from(ethers.toUtf8Bytes(handle)).buffer,
    },
    await backupKey(root),
    Uint8Array.from(plaintext).buffer,
  );
  return ethers.hexlify(ethers.concat([iv, new Uint8Array(ciphertext)]));
}

export async function unwrapHandleSecret(
  root: string,
  handle: string,
  sealed: string,
): Promise<string | null> {
  try {
    const bytes = ethers.getBytes(sealed);
    if (bytes.length <= 12) return null;
    const plaintext = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: Uint8Array.from(bytes.slice(0, 12)).buffer,
        additionalData: Uint8Array.from(ethers.toUtf8Bytes(handle)).buffer,
      },
      await backupKey(root),
      Uint8Array.from(bytes.slice(12)).buffer,
    );
    return plaintext.byteLength === 32
      ? ethers.hexlify(new Uint8Array(plaintext))
      : null;
  } catch {
    return null;
  }
}

interface OwnedHandle {
  handle: string;
  secret: string | null;
}

/** Handles filed under this wallet's lookup key (needs its own session). */
export async function fetchOwnedHandles(
  token: string,
  key: string,
): Promise<OwnedHandle[]> {
  const response = await fetch(
    `/api/handles/lookup?key=${encodeURIComponent(key)}`,
    { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" },
  );
  const data = await response.json().catch(() => null);
  if (!response.ok || data?.success !== true || !Array.isArray(data.handles))
    throw new Error(
      "Could not look up your handle right now. Please try again.",
    );
  return data.handles
    .filter(
      (item: any) =>
        typeof item?.handle === "string" &&
        normalizeHandle(item.handle) === item.handle,
    )
    .map((item: any) => ({
      handle: item.handle,
      secret: typeof item.secret === "string" ? item.secret : null,
    }));
}
